/**
 * Pinterest Conversions API (CAPI) – Client-Side Dispatcher
 *
 * Sends server-side events to the Pinterest CAPI via our edge function.
 * Each event carries an `event_id` that is also passed to the browser
 * tag so Pinterest can deduplicate the pair.
 *
 * Consent-aware: only fires when the Pinterest tag has been initialised
 * (which itself requires marketing consent).
 */

import { isPinterestInitialised } from './pinterest';
import { PINTEREST_AD_ACCOUNT_ID, PINTEREST_CAPI_ENDPOINT } from './config';
import { getAnalyticsUserData } from './userData';
import { getEpik } from '@/lib/utm';

export async function sendPinterestServerEvent(event: {
  event_name: string;
  event_id: string;
  event_source_url?: string;
  // Per Pinterest spec — same matching params as Meta CAPI (em, ph, fn, ln, ct, st, zp)
  // plus external_id. Hashed server-side in the edge function. Cached values from
  // getAnalyticsUserData() are spread first; explicit user_data wins per-event.
  user_data?: {
    em?: string;
    external_id?: string;
    ph?: string;
    fn?: string;
    ln?: string;
    ct?: string;
    st?: string;
    zp?: string;
  };
  custom_data?: Record<string, unknown>;
}): Promise<void> {
  if (!isPinterestInitialised()) {
    if (import.meta.env.DEV) console.debug('[Pinterest CAPI] Skipped: tag not initialised');
    return;
  }
  if (!PINTEREST_AD_ACCOUNT_ID || !PINTEREST_CAPI_ENDPOINT) {
    if (import.meta.env.DEV) console.warn('[Pinterest CAPI] Skipped: missing config');
    return;
  }

  const epik = getEpik();
  const payload = {
    ad_account_id: PINTEREST_AD_ACCOUNT_ID,
    events: [{
      event_name: event.event_name,
      event_id: event.event_id,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: event.event_source_url ?? (typeof window !== 'undefined' ? window.location.href : undefined),
      user_data: {
        client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        ...getAnalyticsUserData(),
        ...(epik ? { click_id: epik } : {}),
        ...event.user_data,
      },
      custom_data: event.custom_data ?? {},
    }],
  };

  try {
    await fetch(PINTEREST_CAPI_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (err) {
    console.warn('[Pinterest CAPI] Failed to send server event:', err);
  }
}
